import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ClipboardIcon } from './icons';
import './CopyListButton.css';

// "Copy list".
//
// This is the transfer mechanism for the whole release. Nothing sends the
// shopping list to a retailer — that needs a supported per-retailer
// integration and does not exist here — so the user moves it themselves, into
// the store's own site, a notes app, or a message to whoever is going. It
// therefore lives on the shopping list itself rather than beside the store
// button: the list is worth copying whether or not we found a shop.
//
// 'manual' is the honest fallback. Clipboard access is refused in plenty of
// ordinary contexts — an insecure origin, an embedded webview, a permission
// the user declined — and a button that quietly does nothing is worse than one
// that admits it. In that case the list is put on screen and selected, for the
// user to copy by hand.
export default function CopyListButton({ text, disabled, className = '' }) {
  const [state, setState] = useState('idle'); // idle | copied | manual
  const areaRef = useRef(null);

  useEffect(() => {
    if (state !== 'copied') return undefined;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  useEffect(() => {
    if (state === 'manual') areaRef.current?.select();
  }, [state]);

  // The text can change under a shown fallback — the user edits an amount
  // while the box is open — so the box is not stale; it renders `text` live.
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('manual');
    }
  }

  return (
    <>
      <button type="button" className={`copy-list ${className}`.trim()} onClick={copy} disabled={disabled}>
        {state === 'copied' ? (
          <>
            <CheckIcon size={15} /> Copied
          </>
        ) : (
          <>
            <ClipboardIcon size={15} /> Copy list
          </>
        )}
      </button>

      {state === 'manual' && (
        <div className="copy-list-manual">
          <p className="copy-list-manual__note">
            Your browser wouldn&rsquo;t let us reach the clipboard. Here&rsquo;s the list — copy it from
            here.
          </p>
          <textarea ref={areaRef} className="copy-list-manual__text" readOnly rows={8} value={text} />
        </div>
      )}
    </>
  );
}
